import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Building2, TrendingUp, Users, Calendar, Mail } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const STEPS = [
  { id: 1, title: "Secteur", icon: Building2 },
  { id: 2, title: "Finances", icon: TrendingUp },
  { id: 3, title: "Équipe", icon: Users },
  { id: 4, title: "Ancienneté", icon: Calendar },
  { id: 5, title: "Contact", icon: Mail },
];

const SECTEURS = [
  { value: "industrie", label: "Industrie / Production" },
  { value: "services", label: "Services aux entreprises" },
  { value: "commerce", label: "Commerce / Distribution" },
  { value: "artisanat", label: "Artisanat" },
  { value: "tech", label: "Tech / Digital" },
  { value: "sante", label: "Santé / Médical" },
  { value: "batiment", label: "BTP / Bâtiment" },
  { value: "restauration", label: "Restauration / Hôtellerie" },
  { value: "autre", label: "Autre" },
];

const CA_OPTIONS = [
  { value: "moins_100k", label: "Moins de 100 000 €" },
  { value: "100k_300k", label: "100 000 € - 300 000 €" },
  { value: "300k_500k", label: "300 000 € - 500 000 €" },
  { value: "500k_1m", label: "500 000 € - 1 M€" },
  { value: "1m_3m", label: "1 M€ - 3 M€" },
  { value: "3m_5m", label: "3 M€ - 5 M€" },
  { value: "plus_5m", label: "Plus de 5 M€" },
];

const RN_OPTIONS = [
  { value: "negatif", label: "Négatif / Perte" },
  { value: "0_50k", label: "0 - 50 000 €" },
  { value: "50k_100k", label: "50 000 € - 100 000 €" },
  { value: "100k_200k", label: "100 000 € - 200 000 €" },
  { value: "200k_500k", label: "200 000 € - 500 000 €" },
  { value: "plus_500k", label: "Plus de 500 000 €" },
];

const EFFECTIF_OPTIONS = [
  { value: "0", label: "0 (solo)" },
  { value: "1_5", label: "1 - 5 salariés" },
  { value: "6_10", label: "6 - 10 salariés" },
  { value: "11_25", label: "11 - 25 salariés" },
  { value: "26_50", label: "26 - 50 salariés" },
  { value: "plus_50", label: "Plus de 50 salariés" },
];

const ANCIENNETE_OPTIONS = [
  { value: "moins_5ans", label: "Moins de 5 ans" },
  { value: "5_10ans", label: "5 - 10 ans" },
  { value: "10_20ans", label: "10 - 20 ans" },
  { value: "plus_20ans", label: "Plus de 20 ans" },
];

const HORIZON_OPTIONS = [
  { value: "moins_6mois", label: "Dans les 6 prochains mois" },
  { value: "6_12mois", label: "Dans 6 à 12 mois" },
  { value: "plus_12mois", label: "Dans plus de 12 mois" },
];

const MOTIVATION_OPTIONS = [
  { value: "retraite", label: "Départ à la retraite" },
  { value: "nouveau_projet", label: "Nouveau projet professionnel" },
  { value: "sante", label: "Raisons de santé" },
  { value: "opportunite", label: "Opportunité de marché" },
  { value: "autre", label: "Autre" },
];

const LOCALISATION_OPTIONS = [
  { value: "aura", label: "Auvergne-Rhône-Alpes" },
  { value: "paca", label: "Provence-Alpes-Côte d'Azur" },
  { value: "occitanie", label: "Occitanie" },
  { value: "autre", label: "Autre région" },
];

export default function CedantSimulator() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    secteur: "",
    chiffre_affaires: "",
    resultat_net: "",
    ebe_n1: "",
    effectif: "",
    anciennete: "",
    email: "",
    prenom: "",
    nom: "",
    telephone: "",
    entreprise: "",
    localisation: "",
    horizon_cession: "",
    motivation: "",
    secteur_activite: "",
  });

  const progress = (currentStep / STEPS.length) * 100;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.secteur !== "";
      case 2:
        return formData.chiffre_affaires !== "" && formData.resultat_net !== "" && formData.ebe_n1 !== "";
      case 3:
        return formData.effectif !== "";
      case 4:
        return formData.anciennete !== "";
      case 5:
        return formData.email !== "" && formData.prenom !== "" && formData.nom !== "";
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // const handleSubmit = async () => {
  //   setIsSubmitting(true);
  //   try {
  //     const payload = {
  //       email: formData.email,
  //       prenom: formData.prenom,
  //       nom: formData.nom || null,
  //       telephone: formData.telephone || null,
  //       entreprise: formData.entreprise || null,
  //       localisation: formData.localisation || null,
  //       horizon_cession: formData.horizon_cession || null,
  //       motivation: formData.motivation || null,
  //       valuation: {
  //         secteur: formData.secteur,
  //         chiffre_affaires: formData.chiffre_affaires,
  //         resultat_net: formData.resultat_net,
  //         effectif: formData.effectif,
  //         anciennete: formData.anciennete,
  //       },
  //     };

  //     const response = await axios.post(`${API}/cedants`, payload);
  //     toast.success("Estimation envoyée avec succès !");
      
  //     // Store result and navigate
  //     localStorage.setItem("cedant_result", JSON.stringify(response.data));
  //     navigate("/cedant/resultat");
  //   } catch (error) {
  //     console.error(error); 
  //     toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

const handleSubmit = async () => {
  setIsSubmitting(true);
  try {
    const payload = {
      email: formData.email,
      prenom: formData.prenom,
      nom: formData.nom || null,
      telephone: formData.telephone || null,
      entreprise: formData.entreprise || null,
      localisation: formData.localisation || null,
      horizon_cession: formData.horizon_cession || null,
      motivation: formData.motivation || null,
      secteur_activite: formData.secteur_activite || null,
      valuation: {
        secteur: formData.secteur,
        chiffre_affaires: formData.chiffre_affaires,
        resultat_net: formData.resultat_net,
        ebe_n1: formData.ebe_n1,
        effectif: formData.effectif,
        anciennete: formData.anciennete,
      },
    };

    // Stocker temporairement les données
    sessionStorage.setItem("temp_user_data", JSON.stringify(payload));
    
    // Naviguer vers SignUp avec le type d'utilisateur
    navigate("/final-signup", { 
      state: { 
        formData: payload,
        userType: "cedant"
      } 
    });
    
  } catch (error) {
    console.error(error);
    toast.error("Erreur lors de la préparation de l'inscription");
  } finally {
    setIsSubmitting(false);
  }
};

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Quel est le secteur d'activité de votre entreprise ? *
              </h2>
              <p className="text-lg text-foreground/60">
                Cette information nous aide à appliquer les bons multiples de valorisation
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {SECTEURS.map((secteur) => (
                <Card
                  key={secteur.value}
                  className={`p-6 cursor-pointer transition-all duration-300 rounded-lg border-2 ${
                    formData.secteur === secteur.value
                      ? "border-accent bg-accent/5 shadow-md"
                      : "border-transparent bg-muted hover:bg-muted/80"
                  }`}
                  onClick={() => handleChange("secteur", secteur.value)}
                  data-testid={`secteur-${secteur.value}`}
                >
                  <p className="text-lg font-semibold text-center text-primary">{secteur.label}</p>
                </Card>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Quelles sont vos données financières ?
              </h2>
              <p className="text-lg text-foreground/60">
                Ces fourchettes nous permettent d'estimer la valeur de votre entreprise
              </p>
            </div>
            <div className="space-y-8 max-w-lg mx-auto">
              <div className="space-y-3">
                <Label className="text-lg font-bold text-primary">Chiffre d'affaires annuel N-1 *</Label>
                <Select
                  value={formData.chiffre_affaires}
                  onValueChange={(value) => handleChange("chiffre_affaires", value)}
                >
                  <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-ca">
                    <SelectValue placeholder="Sélectionnez une fourchette" />
                  </SelectTrigger>
                  <SelectContent>
                    {CA_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                <Label className="text-lg font-bold text-primary">Résultat net *</Label>
                <Select
                  value={formData.resultat_net}
                  onValueChange={(value) => handleChange("resultat_net", value)}
                >
                  <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-rn">
                    <SelectValue placeholder="Sélectionnez une fourchette" />
                  </SelectTrigger>
                  <SelectContent>
                    {RN_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                <Label className="text-lg font-bold text-primary">EBE annuel N-1 *</Label>
                <Select
                  value={formData.ebe_n1}
                  onValueChange={(value) => handleChange("ebe_n1", value)}
                >
                  <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-ebe">
                    <SelectValue placeholder="Sélectionnez une fourchette" />
                  </SelectTrigger>
                  <SelectContent>
                    {RN_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Quelle est la taille de votre équipe ? *
              </h2>
              <p className="text-lg text-foreground/60">
                L'effectif influence la complexité de la transmission
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              {EFFECTIF_OPTIONS.map((option) => (
                <Card
                  key={option.value}
                  className={`p-6 cursor-pointer transition-all duration-300 rounded-lg border-2 ${
                    formData.effectif === option.value
                      ? "border-accent bg-accent/5 shadow-md"
                      : "border-transparent bg-muted hover:bg-muted/80"
                  }`}
                  onClick={() => handleChange("effectif", option.value)}
                  data-testid={`effectif-${option.value}`}
                >
                  <p className="text-lg font-semibold text-center text-primary">{option.label}</p>
                </Card>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Depuis combien de temps existe votre entreprise ? *
              </h2>
              <p className="text-lg text-foreground/60">
                L'ancienneté est un facteur de stabilité et de valeur
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
              {ANCIENNETE_OPTIONS.map((option) => (
                <Card
                  key={option.value}
                  className={`p-6 cursor-pointer transition-all duration-300 rounded-lg border-2 ${
                    formData.anciennete === option.value
                      ? "border-accent bg-accent/5 shadow-md"
                      : "border-transparent bg-muted hover:bg-muted/80"
                  }`}
                  onClick={() => handleChange("anciennete", option.value)}
                  data-testid={`anciennete-${option.value}`}
                >
                  <p className="text-lg font-semibold text-center text-primary">{option.label}</p>
                </Card>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Recevez votre estimation personnalisée
              </h2>
              <p className="text-lg text-foreground/60">
                Nous vous enverrons votre rapport de valorisation détaillé
              </p>
            </div>
            <div className="space-y-6 max-w-xl mx-auto">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="prenom" className="text-lg font-bold text-primary">Prénom *</Label>
                  <Input
                    id="prenom"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={formData.prenom}
                    onChange={(e) => handleChange("prenom", e.target.value)}
                    placeholder="Votre prénom"
                    data-testid="input-prenom"
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="nom" className="text-lg font-bold text-primary">Nom *</Label>
                  <Input
                    id="nom"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={formData.nom}
                    onChange={(e) => handleChange("nom", e.target.value)}
                    placeholder="Votre nom"
                    data-testid="input-nom"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="email" className="text-lg font-bold text-primary">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="votre@email.com"
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="telephone" className="text-lg font-bold text-primary">Téléphone</Label>
                  <Input
                    id="telephone"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={formData.telephone}
                    onChange={(e) => handleChange("telephone", e.target.value)}
                    placeholder="06 XX XX XX XX"
                    data-testid="input-telephone"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="entreprise" className="text-lg font-bold text-primary">Nom de l'entreprise </Label>
                  <Input
                    id="entreprise"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={formData.entreprise}
                    onChange={(e) => handleChange("entreprise", e.target.value)}
                    placeholder="Nom de votre entreprise"
                    data-testid="input-entreprise"
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="secteur_activite" className="text-lg font-bold text-primary">Secteur précis</Label>
                  <Input
                    id="secteur_activite"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={formData.secteur_activite}
                    onChange={(e) => handleChange("secteur_activite", e.target.value)}
                    placeholder="Ex: Boulangerie Pâtisserie"
                    data-testid="input-secteur-activite"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-lg font-bold text-primary">Localisation </Label>
                  <Select
                    value={formData.localisation}
                    onValueChange={(value) => handleChange("localisation", value)}
                  >
                    <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-localisation">
                      <SelectValue placeholder="Région" />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCALISATION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label className="text-lg font-bold text-primary">Horizon de cession </Label>
                  <Select
                    value={formData.horizon_cession}
                    onValueChange={(value) => handleChange("horizon_cession", value)}
                  >
                    <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-horizon">
                      <SelectValue placeholder="Délai" />
                    </SelectTrigger>
                    <SelectContent>
                      {HORIZON_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-3">
                <Label className="text-lg font-bold text-primary">Motivation principale </Label>
                <Select
                  value={formData.motivation}
                  onValueChange={(value) => handleChange("motivation", value)}
                >
                  <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-motivation">
                    <SelectValue placeholder="Pourquoi souhaitez-vous céder ?" />
                  </SelectTrigger>
                  <SelectContent>
                    {MOTIVATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background py-16 px-8" data-testid="cedant-simulator">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-16">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-foreground/60 hover:text-primary transition-colors font-medium"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
          <span className="text-3xl font-bold text-primary">Sequenti</span>
        </div>

        {/* Progress */}
        <div className="mb-16">
          <div className="flex justify-between mb-6">
            {STEPS.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex flex-col items-center transition-opacity duration-300 ${
                    step.id <= currentStep ? "opacity-100" : "opacity-40"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                      step.id <= currentStep ? "bg-primary text-white scale-110 shadow-md" : "bg-muted text-primary"
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className={`text-sm font-bold uppercase tracking-wider hidden md:block ${
                      step.id <= currentStep ? "text-primary" : "text-foreground/40"
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-2 bg-muted transition-all duration-500" />
        </div>

        {/* Step Content */}
        <Card className="p-12 mb-12 border-none bg-white rounded-lg shadow-sm border border-border/40">
          {renderStep()}
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="text-lg px-8 h-14 hover:bg-muted"
            data-testid="prev-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Précédent
          </Button>
          {currentStep < STEPS.length ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-primary text-white hover:bg-primary/90 text-lg px-12 h-14 rounded-lg shadow-md transition-all active:scale-95"
              data-testid="next-button"
            >
              Suivant
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className="bg-accent text-white hover:bg-accent/90 text-lg px-12 h-14 rounded-lg shadow-md transition-all active:scale-95"
              data-testid="submit-button"
            >
              {isSubmitting ? "Envoi en cours..." : "Obtenir mon estimation"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
